import { useEffect, useMemo, useState } from 'react'
import { errorText } from '@forwardflow/features/campaign-flow/utils/errors'
import { useAppStore } from '@forwardflow/store/appStore'
import { useFlowReferenceData } from '@forwardflow/features/campaign-flow/hooks/useFlowReferenceData'
import { useLocation, useNavigate } from 'react-router-dom'
import cn from 'classnames'
import { FlowShell } from '@forwardflow/features/campaign-flow/components/frame/FlowShell'
import { TopBar } from '@forwardflow/features/campaign-flow/components/frame/FlowFrame'
import { ActivityPanel } from '@forwardflow/features/campaign-flow/components/stats/ActivityPanel'
import {
   PeriodSwitch,
   ResultsBlock,
} from '@forwardflow/features/campaign-flow/components/stats/ResultsBlock'
import {
   ShareBar,
   Sparkline,
} from '@forwardflow/features/campaign-flow/components/stats/StatParts'
import { KindTag } from '@forwardflow/features/campaign-flow/components/ui/KindTag'
import { AiOrb } from '@forwardflow/features/campaign-flow/components/ui/AiOrb'
import {
   FlowIcon,
   TFlowIcon,
} from '@forwardflow/features/campaign-flow/components/ui/FlowIcon'
import { Spinner } from '@forwardflow/features/campaign-flow/components/ui/primitives'
import { useFlowBase } from '@forwardflow/features/campaign-flow/hooks/useFlowBase'
import {
   ICampaignRow,
   IOverviewStats,
   TPeriod,
} from '@forwardflow/features/campaign-flow/interfaces/campaign-flow.types'
import {
   campaignFlowService,
   startNewCampaign,
} from '@forwardflow/features/campaign-flow/services'
import { useCampaignFlowStore } from '@forwardflow/features/campaign-flow/store/flow.store'
import { conversationsLink } from '@forwardflow/features/campaign-flow/utils/links'
import {
   percent,
   rate,
   trendLabel,
} from '@forwardflow/features/campaign-flow/utils/format'
import '@forwardflow/features/campaign-flow/components/campaign-flow.css'

type TSort =
   | 'name'
   | 'status'
   | 'leads'
   | 'appointments'
   | 'transfers'
   | 'trend'
   | 'activity'

/** "4 min ago", "Today", "3 days ago" → minutes ago; never-started campaigns sort last. */
const minutesAgo = (text: string) => {
   const t = text.toLowerCase()
   if (/just|now|today/.test(t)) return 0
   if (/yesterday/.test(t)) return 1440
   const m = t.match(/(\d+)\s*(min|minute|hr|hour|h\b|day|week|month)/)
   if (!m) return Number.POSITIVE_INFINITY
   const n = Number(m[1])
   const unit = m[2]
   return unit.startsWith('min')
      ? n
      : /^(hr|hour|h)/.test(unit)
        ? n * 60
        : unit.startsWith('day')
          ? n * 1440
          : unit.startsWith('week')
            ? n * 10080
            : n * 43200
}
const STATUS_ORDER: Record<ICampaignRow['status'], number> = {
   live: 2,
   paused: 1,
   draft: 0,
}
const STATUS: Record<
   ICampaignRow['status'],
   { label: string; icon: TFlowIcon; cls: string }
> = {
   live: {
      label: 'Live',
      icon: 'check',
      cls: 'text-[#B2E58B] border-[#B2E58B]/30 bg-[#B2E58B]/[0.08]',
   },
   paused: {
      label: 'Paused',
      icon: 'pause',
      cls: 'text-[#9ca3af] border-white/15 bg-white/[0.05]',
   },
   draft: {
      label: 'Draft',
      icon: 'pen',
      cls: 'text-[#DAC2FF] border-[#9061DA]/35 bg-[#9061DA]/10',
   },
}

const Segmented = <T extends string>({
   value,
   options,
   onChange,
   testId,
}: {
   value: T
   options: { value: T; label: string }[]
   onChange: (v: T) => void
   testId?: string
}) => (
   <div
      className="flex gap-0.5 bg-white/[0.05] rounded-full p-0.5"
      role="tablist"
      data-testid={testId}
   >
      {options.map((o) => (
         <button
            key={o.value}
            type="button"
            role="tab"
            aria-selected={value === o.value}
            onClick={() => onChange(o.value)}
            className={cn(
               'h-8 px-3.5 rounded-full text-[13px] font-medium whitespace-nowrap transition-colors',
               value === o.value
                  ? 'bg-white/[0.12] text-white'
                  : 'text-[#9ca3af] hover:text-white'
            )}
            data-testid={testId ? `${testId}-${o.value}` : undefined}
         >
            {o.label}
         </button>
      ))}
   </div>
)

/** With one campaign there is no table to compare in: one card that opens it. */
const OneCampaign = ({
   row,
   onOpen,
}: {
   row: ICampaignRow
   onOpen: () => void
}) => {
   const st = STATUS[row.status]
   return (
      <button
         type="button"
         onClick={onOpen}
         className="text-left rounded-[14px] bg-white/[0.04] border border-white/[0.06] hover:border-[#9061DA]/60 p-4 flex items-center gap-4 transition-colors"
         data-testid={`cf-row-${row.id}`}
      >
         <AiOrb hue={row.voiceHue} size={44} animated={false} />
         <span className="min-w-0 flex-1">
            <span className="flex items-center gap-2">
               <b className="font-semibold truncate">{row.name}</b>
               <KindTag kind={row.kind} />
               <span
                  className={cn(
                     'inline-flex items-center gap-1 h-[22px] px-2 rounded-full border text-[10px] font-semibold uppercase tracking-[.05em]',
                     st.cls
                  )}
               >
                  {st.label}
               </span>
            </span>
            <span className="block text-xs text-[#9ca3af] truncate">
               {[row.source, row.voiceName]
                  .filter(Boolean)
                  .map((value) => `${value} · `)
                  .join('')}
               {row.leads.toLocaleString()} leads · {row.lastActivity}
            </span>
         </span>
         <FlowIcon name="chev" size={16} className="text-white/40 shrink-0" />
      </button>
   )
}

/**
 * Campaigns. Results for the period on top (each with its change and trend),
 * how fast leads are handled under it, then every campaign. The guided flow
 * starts from "New campaign".
 */
export const CampaignFlowEntry = () => {
   const org = useAppStore((s) => s.currentOrganization)
   // Remount all organization-scoped results, filters and activity on account changes.
   return <CampaignOverview key={org || 'preview'} />
}

const CampaignOverview = () => {
   useFlowReferenceData()
   const navigate = useNavigate()
   const location = useLocation()
   const [launched, setLaunched] = useState<{
      id: string | null
      name: string
   } | null>(
      (
         location.state as {
            launched?: { id: string | null; name: string }
         } | null
      )?.launched ?? null
   )
   const { base } = useFlowBase()
   const [period, setPeriod] = useState<TPeriod>('30d')
   const [stats, setStats] = useState<IOverviewStats | null>(null)
   const [rows, setRows] = useState<ICampaignRow[] | null>(null)
   const [tab, setTab] = useState<'all' | ICampaignRow['status']>('all')
   const [search, setSearch] = useState('')
   const [sort, setSort] = useState<TSort>('leads')
   /* Click a header to sort by it; click again to flip the order. */
   const [desc, setDesc] = useState(true)
   const sortBy = (k: TSort) => {
      if (sort === k) return setDesc((d) => !d)
      setSort(k)
      /* Names read A to Z first; numbers and recency start with the biggest or newest. */
      setDesc(k !== 'name')
   }

   const [loadError, setLoadError] = useState<string | null>(null)
   const [statsError, setStatsError] = useState<string | null>(null)
   const [reload, setReload] = useState(0)
   const retry = () => setReload((n) => n + 1)
   /* A row opens the campaign's own page (results, activity, setup); Edit lives there. */
   const campaignPath = (id: string) => `${base}/${encodeURIComponent(id)}`

   useEffect(() => {
      let current = true
      setLoadError(null)
      void campaignFlowService
         /* The rows show with the list; a period change keeps the voices while the numbers update. */
         .campaigns(period, (quick) => {
            if (!current) return
            setRows((old) =>
               quick.map((q) => ({
                  ...q,
                  voiceName:
                     old?.find((x) => x.id === q.id)?.voiceName || q.voiceName,
               }))
            )
         })
         .then((result) => current && setRows(result))
         .catch((e: Error) => {
            if (current)
               setLoadError(errorText(e, 'Could not load your campaigns.'))
         })
      return () => {
         current = false
      }
   }, [period, reload])
   useEffect(() => {
      let current = true
      /* A period change keeps the block up until the new numbers arrive. */
      setStatsError(null)
      void campaignFlowService
         .overview(period)
         .then((result) => current && setStats(result))
         .catch((e: Error) => {
            if (current)
               setStatsError(errorText(e, 'Could not load your results.'))
         })
      return () => {
         current = false
      }
   }, [period, reload])

   const labels = useMemo(
      () => Array.from({ length: 12 }, (_, i) => trendLabel(period, i)),
      [period]
   )
   const shown = useMemo(() => {
      const list = (rows ?? []).filter(
         (r) =>
            (tab === 'all' || r.status === tab) &&
            r.name.toLowerCase().includes(search.trim().toLowerCase())
      )
      const key = (r: ICampaignRow): number =>
         ({
            name: 0,
            status: STATUS_ORDER[r.status],
            leads: r.leads,
            appointments: r.appointments,
            transfers: r.transfers,
            trend: r.trend.reduce((a, x) => a + x, 0),
            /* Most recent first when descending. */
            activity: -minutesAgo(r.lastActivity),
         })[sort]
      const cmp = (a: ICampaignRow, b: ICampaignRow) =>
         sort === 'name' ? a.name.localeCompare(b.name) : key(a) - key(b)
      /* A campaign launched just now stays on top. */
      return [...list].sort(
         (a, b) =>
            Number(b.id === launched?.id) - Number(a.id === launched?.id) ||
            (desc ? cmp(b, a) : cmp(a, b))
      )
   }, [rows, tab, search, sort, desc, launched])
   const countOf = (s: ICampaignRow['status'] | 'all') =>
      (rows ?? []).filter((r) => s === 'all' || r.status === s).length

   const SortHead = ({
      k,
      children,
      className,
   }: {
      k: TSort
      children: string
      className?: string
   }) => (
      <th
         className={cn('font-medium px-4 py-2.5', className)}
         aria-sort={
            sort === k ? (desc ? 'descending' : 'ascending') : undefined
         }
      >
         <button
            type="button"
            onClick={() => sortBy(k)}
            className={cn(
               'group inline-flex items-center gap-1 hover:text-white',
               sort === k && 'text-white'
            )}
            data-testid={`cf-sort-${k}`}
            title={`Sort by ${children.toLowerCase()}`}
         >
            {children}
            <FlowIcon
               name="chevDown"
               size={12}
               className={cn(
                  'transition-transform',
                  sort === k
                     ? !desc && 'rotate-180'
                     : 'opacity-0 group-hover:opacity-50'
               )}
            />
         </button>
      </th>
   )

   return (
      <FlowShell>
         <div
            className="cf flex flex-col gap-4 pb-6"
            data-testid="campaign-flow-entry"
         >
            {(loadError || statsError) && (
               <div
                  className="flex items-center gap-3 rounded-[14px] border border-amber-400/30 bg-amber-400/[0.07] px-4 py-3 text-sm"
                  data-testid="cf-load-note"
                  role="status"
               >
                  <span className="flex-1 min-w-0">
                     {loadError || statsError}
                  </span>
                  <button
                     type="button"
                     onClick={retry}
                     className="font-semibold text-[#DAC2FF] hover:text-white"
                  >
                     Retry
                  </button>
               </div>
            )}
            {launched && (
               <div
                  className="flex items-center gap-3 rounded-[14px] border border-[#00d857]/30 bg-[#00d857]/[0.07] px-4 py-3 text-sm"
                  data-testid="cf-launched-banner"
                  role="status"
               >
                  <span className="w-8 h-8 rounded-full bg-[#00d857]/[0.18] text-[#00d857] flex items-center justify-center shrink-0">
                     <FlowIcon name="rocket" size={15} />
                  </span>
                  <span className="flex-1 min-w-0">
                     <b className="font-semibold">{launched.name} is live.</b>{' '}
                     <span className="text-[#9ca3af]">
                        First calls go out during your calling hours.
                     </span>
                  </span>
                  {launched.id && (
                     <button
                        type="button"
                        onClick={() => navigate(campaignPath(launched.id!))}
                        className="text-[13px] font-semibold text-[#DAC2FF] hover:text-white"
                     >
                        Open
                     </button>
                  )}
                  <button
                     type="button"
                     onClick={() => setLaunched(null)}
                     aria-label="Dismiss"
                     className="w-7 h-7 rounded-full flex items-center justify-center text-white/50 hover:text-white hover:bg-white/10"
                  >
                     <FlowIcon name="x" size={14} />
                  </button>
               </div>
            )}
            <TopBar
               name="All campaigns"
               right={
                  <>
                     <span className="hidden md:block">
                        <PeriodSwitch value={period} onChange={setPeriod} />
                     </span>
                     <button
                        type="button"
                        onClick={() => {
                           useCampaignFlowStore.getState().reset()
                           startNewCampaign()
                           navigate(`${base}/new`)
                        }}
                        className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full bg-[#9061DA] hover:bg-[#7044B4] text-[13px] font-semibold"
                        data-testid="cf-new-campaign"
                     >
                        <FlowIcon name="plus" size={14} /> New campaign
                     </button>
                  </>
               }
            />

            {/* Results: the same block as a campaign's own page */}
            {!stats && !statsError ? (
               /* Holds the results block's height so the page keeps its shape while loading. */
               <div className="flex items-center justify-center min-h-[300px]">
                  <Spinner size={22} />
               </div>
            ) : stats ? (
               <ResultsBlock
                  stats={stats}
                  openActivity={(kind) =>
                     navigate(conversationsLink({ kind }))
                  }
               />
            ) : null}

            <div
               className={cn(
                  'grid grid-cols-1 gap-4 items-start',
                  /* Until the rows are known, lay out for the common case (a table
                     plus the side rail) so nothing jumps when they arrive. */
                  rows?.length === 1
                     ? 'lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]'
                     : 'min-[1700px]:grid-cols-[minmax(0,1fr)_340px]'
               )}
            >
               {!rows && !loadError && (
                  <div
                     className="flex justify-center py-12"
                     role="status"
                     aria-label="Loading campaigns"
                  >
                     <Spinner />
                  </div>
               )}
               {rows?.length === 0 && (
                  <section
                     className="rounded-[14px] bg-white/[0.04] border border-white/[0.06] p-8 text-center"
                     data-testid="cf-empty"
                  >
                     <h2 className="text-lg font-semibold">
                        Create your first campaign
                     </h2>
                     <p className="mt-2 text-sm text-[#9ca3af]">
                        Choose your audience, content and cadence to get
                        started.
                     </p>
                     <button
                        type="button"
                        onClick={() => navigate(`${base}/new`)}
                        className="mt-5 h-9 px-4 rounded-full bg-[#9061DA] hover:bg-[#7044B4] text-sm font-semibold"
                     >
                        New campaign
                     </button>
                  </section>
               )}
               {rows && rows.length === 1 && (
                  <OneCampaign
                     row={rows[0]}
                     onOpen={() => navigate(campaignPath(rows[0].id))}
                  />
               )}
               {/* Campaigns: the table earns its place with two or more */}
               {rows && rows.length > 1 && (
                  <section
                     className="rounded-[14px] bg-white/[0.04] border border-white/[0.06] overflow-hidden"
                     data-testid="cf-campaigns"
                  >
                     <div className="flex flex-wrap items-center gap-2.5 px-4 py-3 border-b border-white/[0.06]">
                        <h2 className="text-[15px] font-semibold mr-2">
                           Campaigns
                        </h2>
                        <Segmented
                           value={tab}
                           onChange={setTab}
                           testId="cf-tab"
                           options={(
                              ['all', 'live', 'paused', 'draft'] as const
                           ).map((t) => ({
                              value: t,
                              label: `${t === 'all' ? 'All' : STATUS[t].label} ${rows ? countOf(t) : ''}`,
                           }))}
                        />
                        <label className="ml-auto flex items-center gap-2 h-9 px-3 rounded-full bg-white/[0.05] border border-white/[0.08] text-[#9ca3af] w-full sm:w-[220px]">
                           <FlowIcon name="search" size={14} />
                           <input
                              value={search}
                              onChange={(e) => setSearch(e.target.value)}
                              placeholder="Search"
                              className="bg-transparent outline-none text-sm text-white flex-1 min-w-0"
                              data-testid="cf-search"
                           />
                        </label>
                     </div>
                     {!rows ? (
                        <div className="flex justify-center py-10">
                           <Spinner />
                        </div>
                     ) : (
                        <div className="overflow-x-auto">
                           <table
                              className="w-full text-sm min-w-[860px]"
                              data-testid="cf-campaign-table"
                           >
                              <thead>
                                 <tr className="text-left text-xs text-[#9ca3af] border-b border-white/[0.06]">
                                    <SortHead k="name">Campaign</SortHead>
                                    <SortHead k="status">Status</SortHead>
                                    <SortHead k="leads">Leads</SortHead>
                                    <SortHead k="appointments">
                                       Appointments
                                    </SortHead>
                                    <SortHead k="transfers">Transfers</SortHead>
                                    <SortHead k="trend">
                                       Appointments trend
                                    </SortHead>
                                    <SortHead
                                       k="activity"
                                       className="text-right"
                                    >
                                       Last activity
                                    </SortHead>
                                 </tr>
                              </thead>
                              <tbody>
                                 {shown.map((r) => {
                                    const st = STATUS[r.status]
                                    return (
                                       <tr
                                          key={r.id}
                                          className={cn(
                                             'border-b border-white/[0.05] last:border-0 cursor-pointer transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#9061DA] focus-visible:-outline-offset-2',
                                             r.status === 'live'
                                                ? 'hover:bg-[#9061DA]/[0.14]'
                                                : 'hover:bg-white/[0.03]',
                                             launched?.id === r.id &&
                                                'bg-[#00d857]/[0.06]'
                                          )}
                                          onClick={() =>
                                             navigate(campaignPath(r.id))
                                          }
                                          role="link"
                                          tabIndex={0}
                                          aria-label={`Open ${r.name}`}
                                          onKeyDown={(e) => {
                                             if (
                                                e.key === 'Enter' ||
                                                e.key === ' '
                                             ) {
                                                e.preventDefault()
                                                navigate(campaignPath(r.id))
                                             }
                                          }}
                                          data-testid={`cf-row-${r.id}`}
                                       >
                                          <td className="px-4 py-3">
                                             <div className="flex items-center gap-3">
                                                <AiOrb
                                                   hue={r.voiceHue}
                                                   size={30}
                                                   animated={false}
                                                />
                                                <div className="min-w-0">
                                                   <div className="font-semibold truncate flex items-center gap-2">
                                                      {r.name}
                                                      <KindTag kind={r.kind} />
                                                   </div>
                                                   {/* min-h keeps the row's height while the names still load. */}
                                                   <div className="text-xs text-[#9ca3af] truncate min-h-4">
                                                      {[r.source, r.voiceName]
                                                         .filter(Boolean)
                                                         .join(' · ')}
                                                   </div>
                                                </div>
                                             </div>
                                          </td>
                                          <td className="px-4">
                                             <span
                                                className={cn(
                                                   'inline-flex items-center gap-1.5 h-[26px] pl-1.5 pr-2.5 rounded-full border text-[11px] font-semibold uppercase tracking-[.05em]',
                                                   st.cls
                                                )}
                                             >
                                                <span className="w-4 h-4 rounded-full flex items-center justify-center">
                                                   <FlowIcon
                                                      name={st.icon}
                                                      size={11}
                                                      strokeWidth={2.4}
                                                   />
                                                </span>
                                                {st.label}
                                             </span>
                                          </td>
                                          <td className="px-4">
                                             <div className="tabular-nums font-semibold">
                                                {r.leads.toLocaleString()}
                                             </div>
                                             <div className="flex items-center gap-2 mt-1">
                                                <ShareBar
                                                   part={r.reached}
                                                   whole={r.leads}
                                                />
                                                <span className="text-[11px] text-[#9ca3af] whitespace-nowrap">
                                                   {percent(
                                                      rate(r.reached, r.leads)
                                                   )}{' '}
                                                   reached
                                                </span>
                                             </div>
                                          </td>
                                          <td className="px-4">
                                             <div className="tabular-nums font-semibold">
                                                {r.appointments}
                                             </div>
                                          </td>
                                          <td className="px-4">
                                             <div className="tabular-nums font-semibold">
                                                {r.transfers}
                                             </div>
                                          </td>
                                          <td className="px-4 w-[160px]">
                                             {r.trend.some((v) => v > 0) ? (
                                                <Sparkline
                                                   values={r.trend}
                                                   labels={labels}
                                                   height={30}
                                                   fill={false}
                                                   accent={
                                                      r.status === 'live'
                                                         ? '#9061DA'
                                                         : '#6b7280'
                                                   }
                                                />
                                             ) : (
                                                <span className="text-xs text-[#818181]">
                                                   No appointments
                                                </span>
                                             )}
                                          </td>
                                          <td className="px-4 text-right text-xs text-[#9ca3af] whitespace-nowrap">
                                             {r.lastActivity}
                                             <FlowIcon
                                                name="chev"
                                                size={14}
                                                className="inline ml-2 text-white/30"
                                             />
                                          </td>
                                       </tr>
                                    )
                                 })}
                                 {!shown.length && (
                                    <tr>
                                       <td
                                          colSpan={7}
                                          className="px-4 py-10 text-center text-[#9ca3af]"
                                       >
                                          {search
                                             ? 'No campaigns match your search.'
                                             : 'No campaigns here yet.'}
                                       </td>
                                    </tr>
                                 )}
                              </tbody>
                           </table>
                        </div>
                     )}
                  </section>
               )}
               <ActivityPanel
                  key={reload}
                  campaignNames={Object.fromEntries(
                     (rows ?? []).map((r) => [r.id, r.name])
                  )}
                  open={(kind, leadId, campaignId) =>
                     navigate(
                        conversationsLink({
                           campaignId,
                           campaignName: campaignId
                              ? (rows ?? []).find((r) => r.id === campaignId)
                                   ?.name
                              : undefined,
                           leadId,
                           kind,
                        })
                     )
                  }
               />
            </div>
         </div>
      </FlowShell>
   )
}
